import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { createItem, updateItem, ItemRequestStatus } from "../../api/generics";
import TreasuryAccount, { newTreasuryAccount } from "../../models/treasury";
import Errors, { getFieldErrors } from "../../models/errors";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { emptyValueOnUndefined } from "../../utils/fields";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";

interface TreasuryAccountFormProps {
  initialAccount?: TreasuryAccount;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const TreasuryAccountForm: React.FC<TreasuryAccountFormProps> = ({
  initialAccount,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingAccount, setEditingAccount] = useState<TreasuryAccount>(
    initialAccount ? initialAccount : newTreasuryAccount()
  );
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);

  const isNew = editingAccount.id === undefined;

  const onSave = async () => {
    if (!editingAccount.name || editingAccount.name.trim() === "") {
      setErrors({ name: ["Indique un nombre"] });
      return;
    }
    setSubmitting(true);
    const toSend: TreasuryAccount = {
      ...editingAccount,
      companyId: company.id,
      initialBalance: editingAccount.initialBalance ?? 0,
    };
    let requestPromise: Promise<ItemRequestStatus<TreasuryAccount>>;
    if (isNew) {
      requestPromise = createItem<TreasuryAccount>(
        "/treasury/accounts/create/",
        toSend
      );
    } else {
      requestPromise = updateItem<TreasuryAccount>(
        `/treasury/accounts/${toSend.id}/`,
        toSend
      );
    }

    const requestStatus = await requestPromise;

    if (requestStatus.status !== SUCCESS) {
      if (requestStatus.errors !== undefined) {
        setErrors(requestStatus.errors);
      }
      errorAlert(requestStatus.detail ?? "Ha ocurrido un error!!");
    } else {
      setErrors({});
      onSuccess();
    }
    setSubmitting(false);
  };

  useEffect(() => {
    setEditingAccount(initialAccount ? initialAccount : newTreasuryAccount());
    setErrors({});
  }, [initialAccount]);

  return (
    <fieldset disabled={submitting}>
      <CFormGroup>
        <CRow>
          <CCol md={3}>
            <CLabel>Nombre:</CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              value={emptyValueOnUndefined(editingAccount.name)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditingAccount({ ...editingAccount, name: e.target.value })
              }
              placeholder="Ej: Banco Itaú, Efectivo"
            ></CInput>
            <FieldErrors
              errors={getFieldErrors("name", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={3}>
            <CLabel>Descripción:</CLabel>
          </CCol>
          <CCol>
            <CInput
              type="text"
              value={emptyValueOnUndefined(editingAccount.description)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditingAccount({
                  ...editingAccount,
                  description:
                    e.target.value !== "" ? e.target.value : undefined,
                })
              }
              placeholder="Opcional"
            ></CInput>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={3}>
            <CLabel>Monto inicial:</CLabel>
          </CCol>
          <CCol>
            <CInput
              type="number"
              value={emptyValueOnUndefined(editingAccount.initialBalance)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditingAccount({
                  ...editingAccount,
                  initialBalance:
                    e.target.value !== "" ? Number(e.target.value) : undefined,
                })
              }
              disabled={!isNew}
              placeholder="0"
            ></CInput>
            {!isNew ? (
              <small className="text-muted">
                El monto inicial no se modifica. Para corregir el saldo cargue
                un ajuste manual.
              </small>
            ) : (
              <></>
            )}
            <FieldErrors
              errors={getFieldErrors("initialBalance", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup className="float-right">
        <CButtonGroup>
          <CButton type="button" color="secondary" onClick={onCancel}>
            Atras
          </CButton>
          <CButton
            type="submit"
            color="primary"
            onClick={onSave}
            disabled={submitting}
          >
            {submitting ? (
              <Spinner
                animation="grow"
                style={{ height: "17px", width: "17px", marginRight: "10px" }}
              />
            ) : (
              <></>
            )}
            {submitting ? "Guardando..." : "Guardar"}
          </CButton>
        </CButtonGroup>
      </CFormGroup>
    </fieldset>
  );
};

export default TreasuryAccountForm;
